import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, AlertCircle, Loader2 } from 'lucide-react';

// PipelineRail — the vertical generation spine Ali approved as a mock on 2026-08-16:
// a long line living in the gutter between the content boxes and the menu; one node per
// produced box, at the box's own height; nodes and the line fill green as steps land; the
// active step carries a HORIZONTAL timer chip next to the "در حال ساخت" pill (he rejected
// vertical timers as unreadable). Layout is a per-row grid — node cell + box cell — so
// alignment is structural, never measured.
//
// <PipelineRail>
//   <RailRow status icon timerSec typicalSec finalSec label micro>{the box}</RailRow>
// </PipelineRail>

export function PipelineRail({ children, testId = 'pipeline-rail' }) {
  return <div className="flex flex-col" data-testid={testId}>{children}</div>;
}

function useElapsed(active) {
  const [sec, setSec] = useState(0);
  const startRef = useRef(null);
  useEffect(() => {
    if (!active) { startRef.current = null; return undefined; }
    startRef.current = Date.now();
    setSec(0);
    const id = setInterval(() => setSec(Math.floor((Date.now() - startRef.current) / 1000)), 1000);
    return () => clearInterval(id);
  }, [active]);
  return sec;
}

const fmt = (sec) => (sec < 60 ? `0:${String(sec).padStart(2, '0')}` : `${Math.floor(sec / 60)}:${String(sec % 60).padStart(2, '0')}`);

export function RailRow({
  status = 'pending',      // 'pending' | 'active' | 'done' | 'error'
  label,                   // stage name, used for a11y only (the box itself carries the title)
  typicalSec,
  finalSec,                // frozen elapsed of a finished stage (chip in the box header row)
  micro,                   // rotating micro-status line for the active stage
  progress,                // { done, total } inside the active stage — drives the bar + ETA
  detail,                  // one line naming what is happening right now
  isLast = false,
  stageKey,
  children,
}) {
  const { t } = useTranslation();
  const elapsed = useElapsed(status === 'active');
  const over = status === 'active' && typicalSec && elapsed > typicalSec * 2;
  // Remaining: measured pace, but a SINGLE stall must not poison it. Averaging elapsed/done
  // folds a 2-minute interruption into every future unit and turned a 7:30 estimate into 12:00
  // (Ali, 2026-08-17). So: blend the measured pace with the typical budget, and cap how far the
  // estimate may exceed the budget — a pause is history, not a prediction.
  let remainingSec = null;
  if (status === 'active') {
    const total = progress?.total ?? 0;
    const done = progress?.done ?? 0;
    if (total > 0 && done > 0) {
      const measured = (elapsed / done) * (total - done);
      const budgeted = typicalSec ? Math.max(0, (typicalSec / total) * (total - done)) : measured;
      // Weight the measurement by how much of the stage it actually covers: one finished screen
      // out of six is weak evidence; five out of six is strong.
      const w = Math.min(1, done / total);
      const blended = measured * w + budgeted * (1 - w);
      const ceiling = typicalSec ? typicalSec * 1.5 : blended;
      remainingSec = Math.max(0, Math.round(Math.min(blended, ceiling)));
    } else if (typicalSec) {
      remainingSec = Math.max(0, typicalSec - elapsed);
    }
    if (remainingSec === 0) remainingSec = null;
  }

  let node;
  if (status === 'done') {
    node = (
      <span className="flex h-6 w-6 items-center justify-center rounded-full bg-success text-success-foreground">
        <Check className="h-3.5 w-3.5" />
      </span>
    );
  } else if (status === 'error') {
    node = (
      <span className="flex h-6 w-6 items-center justify-center rounded-full bg-destructive text-destructive-foreground">
        <AlertCircle className="h-3.5 w-3.5" />
      </span>
    );
  } else if (status === 'active') {
    node = (
      <span className="relative flex h-7 w-7 items-center justify-center rounded-full border-2 border-primary bg-card text-primary theater-pulse">
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        <span className="theater-ring rounded-full" aria-hidden="true" />
      </span>
    );
  } else {
    node = <span className="h-5 w-5 rounded-full border-2 border-border bg-card" />;
  }

  return (
    <div className="grid grid-cols-[44px_minmax(0,1fr)] gap-x-2" data-testid={`rail-row-${stageKey}`} aria-label={label}>
      <div className="flex flex-col items-center">
        <div className="flex flex-1 flex-col items-center w-full">
          <div className={`w-1 flex-none h-3 rounded-full ${status === 'done' || status === 'active' ? 'bg-success' : 'bg-border'}`} />
          <div className="my-1">{node}</div>
          {!isLast && (
            <div className={`w-1 flex-1 min-h-[16px] rounded-full ${status === 'done' ? 'bg-success' : 'bg-border'}`} />
          )}
        </div>
      </div>

      <div className={`pb-4 min-w-0 ${status === 'pending' ? 'opacity-55' : ''}`}>
        {status === 'active' && (
          <div
            className={`mb-3 rounded-xl border p-3 ${over ? 'border-warning-border bg-warning-soft' : 'border-primary/40 bg-card'}`}
            data-testid={`rail-active-${stageKey}`}
          >
            <div className="flex items-center gap-2">
              <span className="theater-dot" aria-hidden="true" />
              <p className={`text-sm font-medium ${over ? 'text-warning' : 'text-primary'}`}>
                {over ? t('theater.stillWorking') : t('theater.workingTitle')}
                {progress?.total ? ` — ${t('theater.ofTotal', { done: progress.done, total: progress.total })}` : ''}
              </p>
            </div>
            {detail && <p className="mt-1 text-xs text-muted-foreground">{detail}</p>}
            {progress?.total > 0 && (
              <div className="mt-2 h-1.5 overflow-hidden rounded bg-muted" data-testid={`rail-bar-${stageKey}`}>
                <div
                  className="h-full rounded bg-primary transition-all duration-500"
                  style={{ width: `${Math.round((progress.done / progress.total) * 100)}%` }}
                />
              </div>
            )}
            {/* Elapsed / remaining / typical as LABELLED stats — a bare "1:42 / ~4:00" was the
                thing Ali called uninformative. */}
            <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs" data-testid={`rail-timer-${stageKey}`}>
              <span className="text-foreground">
                {t('theater.elapsed')} <b className="font-data tabular-nums">{fmt(elapsed)}</b>
              </span>
              {remainingSec !== null && (
                <span className="text-foreground">
                  {t('theater.remaining')} <b className="font-data tabular-nums">~{fmt(remainingSec)}</b>
                </span>
              )}
              {typicalSec ? (
                <span className="text-muted-foreground">
                  {t('theater.typical', { time: fmt(typicalSec) })}
                </span>
              ) : null}
            </div>
          </div>
        )}
        {status === 'active' && micro && (
          <p key={micro} className="mb-2 text-xs text-muted-foreground theater-msg">{micro}</p>
        )}
        {status === 'done' && typeof finalSec === 'number' && (
          <div className="mb-1 flex justify-end">
            <span className="inline-flex items-center gap-1 rounded-md bg-success-soft px-2 py-0.5 text-[11px] font-data tabular-nums text-success" data-testid={`rail-final-${stageKey}`}>
              <Check className="h-3 w-3" /> {fmt(finalSec)}
            </span>
          </div>
        )}
        {children}
      </div>
    </div>
  );
}
